import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { MdbCompatibleOption, Truth } from './common/common-types';
import { TestEmailData, CurriculumListData, ModuleListOptionsData, ModuleOption, SafetyReportsListData, UserProfileData, Curriculum, ApiAccessData, ApiClientData, QualityRatings, UserAddressData, HybridLearningCenterData, HybridLearningCenterSaveData, LearningCenterVendorAccessData, SupportContactData } from './learning-center-types';
import { Observable, of } from '../../node_modules/rxjs';
import { RequirementItemsData, CompetencyObjectData } from './competencies/competency-types';
import { map } from 'rxjs/operators';
import { SimpleItemData, SimpleItemTypes } from './common/manage-item-list/manage-item-types';
import { ModuleCopyLogData, ModuleSharingFormData } from './utilities/module-sharing-overview/module-sharing-types';

@Injectable()
export class LearningCenterService {

  userProfiles: Map<number, UserProfileData> = new Map<number, UserProfileData>();
  userProfilesFetched: boolean = false;
  usersNamesLoaded: boolean = false;

  studentOptions: MdbCompatibleOption[] = [];
  studentOptionsFetched: boolean = false;

  studentGroupOptions: MdbCompatibleOption[] = [];
  studentGroupOptionsFetched: boolean = false;

  private cachedModulesList: MdbCompatibleOption[] = null;
  private cachedCurriculumsList: MdbCompatibleOption[] = null;
  private cachedDocumentsList: MdbCompatibleOption[] = null;
  private cachedOfflineEventsList: MdbCompatibleOption[] = null;
  private cachedTrainingLists: RequirementItemsData = null;
  private cachedLearningCentersList: MdbCompatibleOption[] = null;

  private mappedModulesList: Map<number, string> = new Map<number, string>();
  private mappedLearningCentersList: Map<number, string> = new Map<number, string>();

  readonly getUsersIdsUrl: string = '/learningCenter/getUsersIds.json';
  readonly getUsersEmailsUrl: string = '/learningCenter/getUsersEmails.json';
  readonly getUsersProfilesUrl: string = '/learningCenter/getUsersProfiles.json';
  readonly getUserAddressUrl: string = '/learningCenter/getUserAddress.json';
  readonly getCurriculumsUrl: string = '/learningCenter/getCurriculums.json';
  readonly getStudentOptionsUrl: string = '/learningCenter/getStudentList.json';
  readonly getStudentGroupOptionsUrl: string = '/learningCenter/getStudentGroupOptions.json';
  readonly getQualityRatingsUrl: string = '/learningCenter/getQualityRatings.json';
  readonly getTrainingCompletePercentUrl: string = '/learningCenter/getTrainingCompletePercent.json';
  readonly getRegistrarHomepageDataUrl: string = '/learningCenter/getRegistrarHomepageData.json';
  readonly getManagerHomepageDataUrl: string = '/learningCenter/getManagerHomepageData.json';
  readonly getStudentsCoursesDataUrl: string = '/learningCenter/getStudentsCoursesData.json';
  readonly getTokenInventoryUrl: string = '/learningCenter/getTokenInventory.json';
  readonly getModuleListUrl: string = '/learningCenter/getModuleList.json';
  readonly getCompetenciesUrl: string = '/learningCenter/getCompetencies.json';

  readonly getSafetyReportsListUrl: string = '/learningCenter/getSafetyReportsList.json';
  readonly getModulesListUrl: string = '/learningCenter/getModulesList.json';
  readonly getCurriculumListUrl: string = '/curriculum/getCurriculumList.json';
  readonly getDocumentsListUrl: string = '/learningCenter/getDocumentsList.json';
  readonly getOfflineEventsListUrl: string = '/learningCenter/getOfflineEventsList.json';
  readonly getTrainingListsUrl: string = '/learningCenter/getTrainingLists.json';
  readonly getLearningCentersListUrl: string = '/learningCenter/getLearningCentersList.json';

  readonly getApiAccessUrl: string = '/learningCenter/getApiAccess.json';
  readonly toggleApiAccessUrl: string = '/learningCenter/toggleApiAccess.json';
  readonly getApiClientsUrl: string = '/learningCenter/getApiClients.json';
  readonly revokeRefreshTokenUrl: string = '/learningCenter/revokeRefreshToken.json';

  readonly sendTestEmailUrl: string = '/settings/send_test_email';

  readonly getSimpleItemsUrl: string = '/learningCenter/getSimpleItems/';
  readonly saveSimpleItemUrl: string = '/learningCenter/saveSimpleItem/';
  readonly deleteSimpleItemUrl: string = '/learningCenter/deleteSimpleItem/';

  readonly getModuleSharingContractsUrl: string = '/learningCenter/getModuleSharingContracts.json';
  readonly submitModuleSharingRequestUrl: string = '/learningCenter/submitModuleSharingRequest.json';
  readonly saveModuleSharingContractUrl: string = '/learningCenter/saveModuleSharingContract.json';
  readonly revokeModuleSharingContractUrl: string = '/learningCenter/revokeModuleSharingContract.json';

  readonly getModuleCopyLogUrl: string = '/learningCenter/getModuleCopyLog.json';
  readonly getSupportContactsUrl: string = '/learningCenter/getSupportContacts.json';

  readonly fetchTrainingDeveloperListUrl: string = '/learningCenter/getTrainingDevelopers.json';
  readonly fetchInstructorsListUrl: string = '/learningCenter/getInstructors.json';

  readonly getHybridLearningCentersUrl: string = '/learningCenter/getHybridLearningCenters.json';
  readonly saveHybridLearningCenterSettingsUrl: string = '/learningCenter/saveHybridLearningCenterSettings.json';
  readonly saveLearningCenterVendorAccessUrl: string = '/learningCenter/saveLearningCenterVendorAccess.json';

  constructor(private http: HttpClient) {}

  fetchUsersNames() {
    if (!this.userProfilesFetched) {
      this.getUsersProfiles().subscribe(data => {
        data.forEach(user => this.userProfiles.set(user.id, user));
        this.usersNamesLoaded = true;
      });

      this.userProfilesFetched = true;
    }
  }

  getStudentOptions() {
    if (!this.userProfilesFetched) this.fetchUsersNames();

    if (!this.studentOptionsFetched) {
      this.studentOptionsFetched = true;

      this.fetchStudentOptions().subscribe(data => {
        this.studentOptions = data.map(id => {
          return {
            label: this.getUsersNameFromId(id).toString(),
            value: id.toString()
          };
        });

        return this.studentOptions;
      });
    }

    return this.studentOptions;
  }

  getStudentGroupOptions() {
    if (!this.studentGroupOptionsFetched) {
      this.studentGroupOptionsFetched = true;

      this.fetchStudentGroupOptions().subscribe(data => {
        this.studentGroupOptions = data;

        return this.studentGroupOptions;
      });
    }

    return this.studentGroupOptions;
  }

  fetchStudentGroupOptions() {
    return this.http.get<MdbCompatibleOption[]>(this.getStudentGroupOptionsUrl);
  }

  fetchStudentOptions() {
    return this.http.get<number[]>(this.getStudentOptionsUrl);
  }

  getUserProfileFromId(id: number) {
    return this.userProfiles.get(id);
  }

  getUsersNameFromId(id: number) {
    let user = this.userProfiles.get(id);

    if (user) return user.firstName + ' ' + user.lastName;
    else return false;
  }

  getUsersAvatarFromId(id: number) {
    let user = this.userProfiles.get(id);

    if (user) return user.avatar;
    else return false;
  }

  getUsersEmailFromId(id: number) {
    let user = this.userProfiles.get(id);

    if (user) return user.email;
    else return false;
  }

  getUsersProfiles(groupID?: string): Observable<UserProfileData[]> {
    const params = groupID ? { groupID: groupID } : {};
    return this.http.get<UserProfileData[]>(this.getUsersProfilesUrl, { params });
  }

  getUsersIds() {
    return this.http.get<MdbCompatibleOption[]>(this.getUsersIdsUrl);
  }

  getUsersEmails() {
    return this.http.get<MdbCompatibleOption[]>(this.getUsersEmailsUrl);
  }

  getUserAddress(userId: number) {
    return this.http.post<UserAddressData>(this.getUserAddressUrl, {userId: userId});
  }

  getCurriculumList() {
    return this.http.get<CurriculumListData[]>(this.getCurriculumListUrl);
  }

  getCurriculums() {
    return this.http.get<Curriculum[]>(this.getCurriculumsUrl);
  }

  sendTestEmail(testEmailData: TestEmailData) {
    return this.http.post<boolean>(this.sendTestEmailUrl, testEmailData);
  }

  getModuleList(moduleListOptionsData: ModuleListOptionsData): Observable<ModuleOption[]> {
    return this.http.post<ModuleOption[]>(this.getModulesListUrl, moduleListOptionsData);
  }

  getLearningCentersList(): Observable<MdbCompatibleOption[]> {
    return this.http.get<MdbCompatibleOption[]>(this.getLearningCentersListUrl);
  }

  getSafetyReportsList() {
    return this.http.get<SafetyReportsListData[]>(this.getSafetyReportsListUrl);
  }

  getApiAccess(): Observable<ApiAccessData> {
    return this.http.get<ApiAccessData>(this.getApiAccessUrl);
  }

  toggleApiAccess(apiEnabled: Truth) : Observable<ApiAccessData> {
    return this.http.post<ApiAccessData>(this.toggleApiAccessUrl, {apiEnabled: apiEnabled});
  }

  getApiClients() {
    return this.http.get<ApiClientData[]>(this.getApiClientsUrl);
  }

  revokeRefreshToken(refresh_token: string) {
    return this.http.post(this.revokeRefreshTokenUrl, {refresh_token: refresh_token});
  }

  getQualityRatings() {
    return this.http.get<QualityRatings>(this.getQualityRatingsUrl);
  }

  getTrainingPercentComplete() {
    return this.http.get<number>(this.getTrainingCompletePercentUrl);
  }

  // Individual API methods for better performance
  getRegistrarHomepageData() {
    return this.http.get<any>(this.getRegistrarHomepageDataUrl);
  }
  getManagerHomepageData() {
    return this.http.get<any>(this.getManagerHomepageDataUrl);
  }

  getStudentsCoursesData() {
    return this.http.get<any[]>(this.getStudentsCoursesDataUrl);
  }

  getTrainingCompletePercent() {
    return this.http.get<{percent: number}>(this.getTrainingCompletePercentUrl);
  }

  getTokenInventory() {
    return this.http.get<any>(this.getTokenInventoryUrl);
  }

  getModuleListSimple() {
    return this.http.get<any[]>(this.getModuleListUrl);
  }

  getCurriculumListSimple() {
    return this.http.get<any[]>(this.getCurriculumListUrl);
  }


  getCompetencies() {
    return this.http.get<CompetencyObjectData[]>(this.getCompetenciesUrl);
  }

  /**
   * Load all training data progressively for better performance
   * This replaces the single heavy getTrainingData call
   */
  loadTrainingDataProgressively() {
    return {
      // Load essential data first (fast)
      essential: {
        registrarHomepageData: this.getRegistrarHomepageData(),
        managerHomepageData: this.getManagerHomepageData(),
        trainingCompletePercent: this.getTrainingCompletePercent(),
        tokenInventory: this.getTokenInventory()
      },
      // Load secondary data (medium)
      secondary: {
        moduleList: this.getModuleListSimple(),
        curriculumList: this.getCurriculumListSimple()
      },
      // Load heavy data last (slow)
      heavy: {
        studentsCoursesData: this.getStudentsCoursesData()
      }
    };
  }

  /**
   * Load only essential data for initial page load
   */
  loadEssentialTrainingData() {
    return {
      registrarHomepageData: this.getRegistrarHomepageData(),
      managerHomepageData: this.getManagerHomepageData(),
      trainingCompletePercent: this.getTrainingCompletePercent(),
      tokenInventory: this.getTokenInventory()
    };
  }

  getTrainingLists() {
    let moduleListOptionsData: ModuleListOptionsData = {
      includeArchived: false,
      includeDraft: false,
      includePublished: true,
      filterExpress: true,
    };

    return this.http.post<RequirementItemsData>(this.getTrainingListsUrl, moduleListOptionsData);
  }

  getDocumentsList() {
    return this.http.get<MdbCompatibleOption[]>(this.getDocumentsListUrl);
  }

  getOfflineEventsList() {
    return this.http.get<MdbCompatibleOption[]>(this.getOfflineEventsListUrl);
  }

  getCachedModulesList(): Observable<MdbCompatibleOption[]> {
    if (this.cachedModulesList === null) {
      let moduleOptions: ModuleListOptionsData = {
        includeArchived: false,
        includeDraft: true,
        includePublished: true
      };

      return this.getModuleList(moduleOptions).pipe(
        map(modulesList => {
          this.cachedModulesList = modulesList;
          modulesList.forEach(module => this.mappedModulesList.set(parseInt(module.value), module.label));

          return modulesList;
        })
      )
    }
    else {
      return of(this.cachedModulesList);
    }
  }

  getModuleName(moduleId: number) {
    return this.mappedModulesList.get(moduleId);
  }

  getCachedLearningCentersList(): Observable<MdbCompatibleOption[]> {
    if (this.cachedLearningCentersList === null) {
      return this.getLearningCentersList().pipe(
        map(lcList => {
          lcList.sort((a, b) => a.label.localeCompare(b.label));

          this.cachedLearningCentersList = lcList;
          lcList.forEach(learningCenter => this.mappedLearningCentersList.set(parseInt(learningCenter.value), learningCenter.label));

          return lcList;
        })
      )
    }
    else {
      return of(this.cachedLearningCentersList);
    }
  }

  getLearningCenterName(lcId: number) {
    return this.mappedLearningCentersList.get(lcId);
  }

  getCachedCurriculumsList(): Observable<MdbCompatibleOption[]> {
    if (this.cachedCurriculumsList === null) {
      return this.getCurriculumList().pipe(
        map(curriculumList => {
          this.cachedCurriculumsList = curriculumList.map(curriculumItem => {
            let newCurriculumItem: MdbCompatibleOption = {
              label: curriculumItem.Curriculum.name, value: curriculumItem.Curriculum.id.toString()
            };

            return newCurriculumItem;
          });

          return this.cachedCurriculumsList;
        })
      )
    }
    else {
      return of(this.cachedCurriculumsList);
    }
  }

  getCachedDocumentsList(): Observable<MdbCompatibleOption[]> {
    if (this.cachedDocumentsList === null) {
      return this.getDocumentsList().pipe(
        map(documentsList => {
          this.cachedDocumentsList = documentsList;
          return documentsList;
        })
      )
    }
    else {
      return of(this.cachedDocumentsList);
    }
  }

  getCachedOfflineEventsList(): Observable<MdbCompatibleOption[]> {
    if (this.cachedOfflineEventsList === null) {
      return this.getOfflineEventsList().pipe(
        map(eventsList => {
          this.cachedOfflineEventsList = eventsList;
          return eventsList;
        })
      )
    }
    else {
      return of(this.cachedOfflineEventsList);
    }
  }

  getCachedTrainingLists(): Observable<RequirementItemsData> {
    if (this.cachedTrainingLists === null) {
      return this.getTrainingLists().pipe(
        map(trainingLists => {
          trainingLists.Olte.sort((a, b) => a.label.toString().trim().localeCompare(b.label.toString().trim()));
          trainingLists.Module.sort((a, b) => a.label.trim().localeCompare(b.label.trim()));
          trainingLists.Document.sort((a, b) => a.label.trim().localeCompare(b.label.trim()));
          trainingLists.Curriculum.sort((a, b) => a.label.trim().localeCompare(b.label.trim()));

          this.cachedTrainingLists = trainingLists;
          return this.cachedTrainingLists;
        })
      )
    }
    else {
      return of(this.cachedTrainingLists);
    }
  }

  getSimpleItems(simpleItemType: SimpleItemTypes): Observable<SimpleItemData[]> {
    return this.http.get<SimpleItemData[]>(this.getSimpleItemsUrl + simpleItemType + '.json');
  }

  saveSimpleItem(simpleItemType: SimpleItemTypes, simpleItem: SimpleItemData): Observable<SimpleItemData> {
    return this.http.post<SimpleItemData>(this.saveSimpleItemUrl + simpleItemType + '.json', simpleItem);
  }

  deleteSimpleItem(simpleItemType: SimpleItemTypes, id: number): Observable<any> {
    return this.http.post<any>(this.deleteSimpleItemUrl + simpleItemType + '.json', {id: id});
  }

  submitModuleSharingRequest(moduleSharingForm: ModuleSharingFormData): Observable<ModuleSharingFormData> {
    return this.http.post<ModuleSharingFormData>(this.submitModuleSharingRequestUrl, moduleSharingForm);
  }

  saveModuleSharingContract(moduleSharingForm: ModuleSharingFormData): Observable<ModuleSharingFormData> {
    return this.http.post<ModuleSharingFormData>(this.saveModuleSharingContractUrl, moduleSharingForm);
  }

  revokeModuleSharingContract(sharingContractId: number): Observable<ModuleSharingFormData> {
    return this.http.post<any>(this.revokeModuleSharingContractUrl, {sharingContractId: sharingContractId});
  }

  getModuleSharingContracts(): Observable<ModuleSharingFormData[]> {
    return this.http.get<ModuleSharingFormData[]>(this.getModuleSharingContractsUrl);
  }

  getModuleCopyLog(): Observable<ModuleCopyLogData[]> {
    return this.http.get<ModuleCopyLogData[]>(this.getModuleCopyLogUrl);
  }

  getHybridLearningCenters(): Observable<HybridLearningCenterData[]> {
    return this.http.get<HybridLearningCenterData[]>(this.getHybridLearningCentersUrl);
  }
  getHybridLearningCentersFromVendor(networkId?): Observable<HybridLearningCenterData[]> {
    return this.http.get<HybridLearningCenterData[]>(this.getHybridLearningCentersUrl+ '?networkId=' + networkId);
  }
  saveHybridLearningCenterSettings(hybridLearningCenterSettings: HybridLearningCenterSaveData[]) {
    return this.http.post<HybridLearningCenterSaveData[]>(this.saveHybridLearningCenterSettingsUrl, hybridLearningCenterSettings);
  }

  saveLearningCenterVendorAccess(learningCenterVendorAccess: LearningCenterVendorAccessData[]) {
    return this.http.post<LearningCenterVendorAccessData[]>(this.saveLearningCenterVendorAccessUrl, learningCenterVendorAccess);
  }

  getSupportContacts(): Observable<SupportContactData[]> {
    return this.http.get<SupportContactData[]>(this.getSupportContactsUrl);
  }

  fetchTrainingDevelopersList(): Observable<number[]> {
    return this.http.get<number[]>(this.fetchTrainingDeveloperListUrl);
  }
  fetchInstructorsList(): Observable<number[]> {
    return this.http.get<number[]>(this.fetchInstructorsListUrl);
  }
}

let lcServiceStub: Partial<LearningCenterService> = {};
export { lcServiceStub };
